import React, { useMemo, useState } from "react";
import type { Theme } from "../lib/theme";
import type { AltRollup, AltStatus } from "../lib/alternates";
import { ALT_STATUSES, isLinePackage } from "../lib/alternates";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

/**
 * The alternates, as decisions.
 *
 * One row per package rather than per line, because the owner is being asked
 * one question per package. The lines are still there -- a package opens to
 * show what it is made of, and every line still links back to the estimate --
 * but they are detail under an answer, not the answer.
 *
 * This replaces three stacked sections (adds, deducts, rejected) that each ran
 * the full length of their list. On a project with forty tagged lines that was
 * a very long page with no way to shorten it. Collapsed by default, filtered
 * by status and searchable, the same project is a dozen rows.
 */
export default function AlternatePackageList({
  rollups, T, fmt, navigateToItem, onStatus, onRename, onCombine, onSplit,
}: {
  rollups: AltRollup[];
  T: Theme;
  fmt: (n: number) => string;
  navigateToItem: (id: string) => void;
  onStatus: (pkgId: string, status: AltStatus) => void;
  onRename: (pkgId: string, name: string) => void;
  /** Fold the given packages' lines into one. */
  onCombine: (pkgIds: string[], name: string) => void;
  onSplit: (pkgId: string) => void;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<"All" | AltStatus>("All");
  const [q, setQ] = useState("");

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rollups.filter(r => {
      if (status !== "All" && r.pkg.status !== status) return false;
      if (!needle) return true;
      // The lines are searched too: what you remember is often a line inside
      // the package rather than what the package was called.
      return r.pkg.name.toLowerCase().includes(needle)
        || r.lines.some(l => l.desc.toLowerCase().includes(needle) || l.div.toLowerCase().includes(needle));
    });
  }, [rollups, status, q]);

  const flip = (set: Set<string>, id: string) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  };

  const tone = (s: AltStatus) =>
    s === "Accepted" ? T.positive : s === "Rejected" ? T.textWeak : T.costStatus["Needs Review"].badgeText;

  const pill = (active: boolean): React.CSSProperties => ({
    fontSize: 10, padding: "4px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer", fontFamily: FONT,
    border: `1px solid ${active ? T.borderAccent : T.borderSecondary}`,
    background: active ? T.bgAccentLight : "transparent",
    color: active ? T.borderAccent : T.textWeak, fontWeight: active ? 600 : 400,
  });

  const pickedRollups = rollups.filter(r => picked.has(r.pkg.id));
  const combine = () => {
    // Named after the biggest piece, because that is almost always the scope
    // the others hang off. Renameable inline the moment it exists.
    const name = [...pickedRollups].sort((a, b) => Math.abs(b.net) - Math.abs(a.net))[0]?.pkg.name || "New alternate";
    onCombine(pickedRollups.map(r => r.pkg.id), name);
    setPicked(new Set());
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4 }}>
          {(["All", ...ALT_STATUSES] as const).map(s => (
            <button key={s} onClick={() => setStatus(s as any)} style={pill(status === s)}>
              {s === "TBD" ? "Undecided" : s}
              {s !== "All" && (
                <span style={{ opacity: .6, marginLeft: 5 }}>
                  {rollups.filter(r => r.pkg.status === s).length}
                </span>
              )}
            </button>
          ))}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search alternates…"
          style={{ flex: 1, minWidth: 160, maxWidth: 300, minHeight: 24, fontSize: 11, fontFamily: FONT,
            background: T.bgInput, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary,
            borderRadius: 3, padding: "4px 9px", outline: "none" }} />
        <button onClick={() => setOpen(open.size ? new Set() : new Set(shown.map(r => r.pkg.id)))}
          style={pill(false)}>{open.size ? "Collapse all" : "Expand all"}</button>
      </div>

      {picked.size > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "9px 14px",
          background: T.bgAccentLight, border: `1px solid ${T.borderAccent}44`, borderRadius: 6 }}>
          <span style={{ fontSize: 11, color: T.textSecondary }}>
            {picked.size} selected · {fmt(pickedRollups.reduce((a, r) => a + r.net, 0))} net
          </span>
          <button onClick={combine} disabled={picked.size < 2}
            style={{ ...pill(picked.size >= 2), marginLeft: "auto", cursor: picked.size < 2 ? "default" : "pointer",
              opacity: picked.size < 2 ? .45 : 1 }}>
            Combine into one alternate
          </button>
          <button onClick={() => setPicked(new Set())} style={pill(false)}>Clear</button>
        </div>
      )}

      {shown.length === 0 && (
        <div style={{ padding: "22px 16px", textAlign: "center", border: `1px dashed ${T.borderSecondary}`,
          borderRadius: 6, fontSize: 11, color: T.textWeak }}>
          {rollups.length === 0 ? "No alternates yet." : "Nothing matches that filter."}
        </div>
      )}

      {shown.map(r => {
        const isOpen = open.has(r.pkg.id);
        const grouped = !isLinePackage(r.pkg.id);
        return (
          <div key={r.pkg.id} style={{ border: `1px solid ${picked.has(r.pkg.id) ? `${T.borderAccent}77` : T.borderPrimary}`,
            borderRadius: 6, marginBottom: 8, background: T.bgPanel, overflow: "hidden" }}>
            <div onClick={() => setOpen(p => flip(p, r.pkg.id))}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer" }}>
              <input type="checkbox" checked={picked.has(r.pkg.id)} onClick={e => e.stopPropagation()}
                onChange={() => setPicked(p => flip(p, r.pkg.id))}
                title="Select to combine with others"
                style={{ cursor: "pointer", accentColor: T.borderAccent, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: T.textWeak, width: 10, flexShrink: 0,
                transform: isOpen ? "rotate(90deg)" : "none", display: "inline-block" }}>&#9654;</span>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.pkg.name}</div>
                {/* What the scope is made of, and what the money is made of --
                    never a bare item count. A count of lines says nothing
                    about the size of the decision, and six lines describing
                    one bathroom used to read as six alternates. */}
                <div style={{ fontSize: 10, color: T.textWeak, marginTop: 2 }}>
                  {grouped ? `${r.lines.length} line${r.lines.length === 1 ? "" : "s"}` : r.lines[0]?.div || "—"}
                  {/* Only when the net is actually hiding something. A pure
                      deduct has gross 0, and "$0 less $119,000 credit" says
                      nothing except that the sentence was built from the
                      wrong halves. */}
                  {r.gross > 0 && r.credits > 0 && <> · {fmt(r.gross)} less {fmt(r.credits)} credit</>}
                  {/* Said out loud rather than resolved quietly: some of these
                      lines are in the bid and some are not, so the scope as
                      priced is not the scope as described. */}
                  {r.mixed && (
                    <span style={{ color: T.costStatus["Needs Review"].badgeText, fontWeight: 600, marginLeft: 6 }}>
                      · Partly accepted — set a status to settle it
                    </span>
                  )}
                </div>
              </div>

              <div style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
                color: r.pkg.status === "Rejected" ? T.textWeak : r.net >= 0 ? T.negative : T.positive }}>
                {r.net >= 0 ? "+" : "−"}{fmt(Math.abs(r.net))}
              </div>

              <select value={r.pkg.status} onClick={e => e.stopPropagation()}
                onChange={e => onStatus(r.pkg.id, e.target.value as AltStatus)}
                style={{ fontSize: 10, fontFamily: FONT, minHeight: 24, background: T.bgInput,
                  border: `1px solid ${tone(r.pkg.status)}44`, color: tone(r.pkg.status), fontWeight: 600,
                  borderRadius: 3, padding: "3px 6px", cursor: "pointer", flexShrink: 0, outline: "none" }}>
                {ALT_STATUSES.map(s => (
                  <option key={s} value={s} style={{ background: T.bgInput, color: T.textSecondary }}>
                    {s === "TBD" ? "Undecided" : s}
                  </option>
                ))}
              </select>
            </div>

            {isOpen && (
              <div style={{ borderTop: `1px solid ${T.borderPrimary}`, padding: "8px 14px 10px 40px" }}>
                {grouped && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <input defaultValue={r.pkg.name} onBlur={e => onRename(r.pkg.id, e.target.value)}
                      placeholder="What the owner is being asked"
                      style={{ flex: 1, fontSize: 11, fontFamily: FONT, minHeight: 24, background: T.bgInput,
                        border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, borderRadius: 3,
                        padding: "4px 8px", outline: "none" }} />
                    <button onClick={() => onSplit(r.pkg.id)} style={pill(false)}
                      title="Break this back into separate alternates">Split apart</button>
                  </div>
                )}
                {r.lines.map(l => (
                  <div key={l.id} onClick={() => navigateToItem(l.id)} title="Open this line in the Estimate"
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 6px", fontSize: 11,
                      cursor: "pointer", borderRadius: 3 }}
                    onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                    onMouseLeave={e => (e.currentTarget.style.background = "")}>
                    <span style={{ fontSize: 10, color: T.textWeak, width: 58, flexShrink: 0,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.div}</span>
                    <span style={{ flex: 1, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap" }}>{l.desc || "Untitled"}</span>
                    <span style={{ color: l.amt < 0 ? T.positive : T.textTertiary, fontWeight: 600,
                      fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                      {l.amt < 0 ? "−" : ""}{fmt(Math.abs(l.amt))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
